import type { TriviaPackSummary, TriviaQuestionDraft } from "./types";

export type LocalTriviaDraft = {
  packId: string;
  title: string;
  questions: TriviaQuestionDraft[];
  activeIndex: number;
};

/** Defaults, a title, and answer/timer selections alone do not make a draft. */
export function questionHasContent(question: TriviaQuestionDraft): boolean {
  return [question.prompt, question.explanation, question.imageUrl, ...question.options, ...(question.optionImageUrls || [])]
    .some((value) => Boolean(value?.trim()));
}

export function meaningfulQuestions(questions: TriviaQuestionDraft[]): TriviaQuestionDraft[] {
  return questions.filter(questionHasContent);
}

export function questionIssue(question: TriviaQuestionDraft): string | null {
  if (!question.prompt.trim() && !question.imageUrl?.trim()) return "Add question text or an image.";
  if (question.options.length < 2 || question.options.length > 6) return "Add between 2 and 6 answers.";
  const emptyAnswer = question.options.findIndex((option, index) => !option.trim() && !question.optionImageUrls?.[index]?.trim());
  if (emptyAnswer !== -1) return `Add text or an image for answer ${String.fromCharCode(65 + emptyAnswer)}.`;
  if (!Number.isInteger(question.correctOptionIndex) || question.correctOptionIndex < 0 || question.correctOptionIndex >= question.options.length) return "Choose the correct answer.";
  return null;
}

export function normaliseQuestion(question: TriviaQuestionDraft): TriviaQuestionDraft {
  // Select current fields so older saved questions lose retired metadata when
  // restored, duplicated, or sent back to the server.
  return {
    ...(question.imageAlt !== undefined ? { imageAlt: question.imageAlt } : {}),
    ...(question.optionImageAlts ? { optionImageAlts: question.options.map((_, index) => question.optionImageAlts?.[index] || "") } : {}),
    prompt: question.prompt,
    options: [...question.options],
    imageUrl: question.imageUrl || "",
    optionImageUrls: question.options.map((_, index) => question.optionImageUrls?.[index] || ""),
    correctOptionIndex: question.correctOptionIndex,
    explanation: question.explanation,
    durationSeconds: question.durationSeconds,
  };
}

export function blankQuestion(): TriviaQuestionDraft {
  return { prompt: "", options: ["", "", "", ""], optionImageUrls: ["", "", "", ""], imageUrl: "", correctOptionIndex: 0, explanation: "", durationSeconds: 30 };
}

function isQuestion(value: unknown): value is TriviaQuestionDraft {
  if (!value || typeof value !== "object") return false;
  const item = value as Record<string, unknown>;
  return typeof item.prompt === "string" && typeof item.explanation === "string"
    && Array.isArray(item.options) && item.options.length >= 2 && item.options.length <= 6 && item.options.every((option) => typeof option === "string")
    && Number.isInteger(item.correctOptionIndex) && Number(item.correctOptionIndex) >= 0 && Number(item.correctOptionIndex) < item.options.length
    && typeof item.durationSeconds === "number" && item.durationSeconds >= 10 && item.durationSeconds <= 90
    && (item.imageAlt === undefined || typeof item.imageAlt === "string")
    && (item.optionImageAlts === undefined || (Array.isArray(item.optionImageAlts) && item.optionImageAlts.every((alt) => typeof alt === "string")))
    && (item.imageUrl === undefined || typeof item.imageUrl === "string")
    && (item.optionImageUrls === undefined || (Array.isArray(item.optionImageUrls) && item.optionImageUrls.every((url) => typeof url === "string")));
}

/** Recover only known, structurally valid drafts; partial text is deliberately retained. */
export function readLocalDrafts(value: string | null): LocalTriviaDraft[] {
  try {
    const decoded: unknown = JSON.parse(value || "null");
    if (!decoded || typeof decoded !== "object" || !("version" in decoded) || decoded.version !== 1 || !("drafts" in decoded) || !Array.isArray(decoded.drafts)) return [];
    return decoded.drafts.filter((item): item is LocalTriviaDraft => Boolean(item) && typeof item === "object"
      && typeof item.packId === "string" && item.packId.length > 0 && typeof item.title === "string"
      && Array.isArray(item.questions) && item.questions.length > 0 && item.questions.length <= 20 && item.questions.every(isQuestion))
      .map((item) => {
        const questions = meaningfulQuestions(item.questions).map(normaliseQuestion);
        return { packId: item.packId, title: item.title, questions, activeIndex: Number.isInteger(item.activeIndex) ? Math.max(0, Math.min(questions.length - 1, item.activeIndex)) : 0 };
      })
      .filter((item) => item.questions.length > 0);
  } catch { return []; }
}

export function rememberDraft(drafts: LocalTriviaDraft[], current: LocalTriviaDraft): LocalTriviaDraft[] {
  const questions = meaningfulQuestions(current.questions);
  if (!questions.length) return drafts;
  return [{ ...current, questions }, ...drafts.filter((draft) => draft.packId !== current.packId)];
}

/** The save acknowledgement already confirms these fields; no library reread is needed. */
export function reconcileSavedDraft(packs: TriviaPackSummary[], draft: LocalTriviaDraft, updatedAt = Date.now()): TriviaPackSummary[] {
  const questions = meaningfulQuestions(draft.questions);
  if (!questions.length) return packs;
  const previous = packs.find((pack) => pack.id === draft.packId);
  const summary: TriviaPackSummary = {
    id: draft.packId,
    title: draft.title.trim() || "Untitled trivia set",
    questionCount: questions.length,
    complete: questions.every((question) => !questionIssue(question)),
    updatedAt,
    lastHostedAt: previous?.lastHostedAt ?? null,
    timesHosted: previous?.timesHosted ?? 0,
  };
  return [summary, ...packs.filter((pack) => pack.id !== draft.packId)];
}
